import React, { useEffect, useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SvgLoader from '../common/components/Loader';
import AuthData from '../common/AuthData';
import Fetch from '../services/fetchService';

const ecommerceQuotes = [
  { quote: "Shop smart. Live better.", tagline: "Your pathway to smarter choices" },
  { quote: "Turning clicks into happiness.", tagline: "Every purchase tells a story" },
  { quote: "Where inspiration meets checkout.", tagline: "Discover what moves you" },
  { quote: "Discover more. Spend less.", tagline: "Value that speaks volumes" },
  { quote: "Designed for dreamers — delivered to doers.", tagline: "From vision to reality" },
  { quote: "Quality delivered to your door.", tagline: "Excellence, every time" },
  { quote: "Find it. Love it. Get it.", tagline: "Simple as that" },
  { quote: "Shopping made simple — success made easy.", tagline: "Your journey starts here" },
  { quote: "Where every purchase feels like a win.", tagline: "Celebrate every click" },
  { quote: "Click, relax, enjoy.", tagline: "The new way to shop" },
  { quote: "Built on trust. Delivered with care.", tagline: "Your satisfaction, our mission" },
  { quote: "Shop confidently, every time.", tagline: "We've got your back" },
  { quote: "Big ideas. Small prices.", tagline: "Dream bigger, spend smarter" },
  { quote: "Simple shopping. Serious value.", tagline: "No compromise needed" },
  { quote: "The future of shopping — today.", tagline: "Stay ahead of the curve" },
];

const Login = ({message}) => {
  const { login, redirectPath } = useAuth();
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState(null); 
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuote, setCurrentQuote] = useState(0);

  useEffect(() => {
    const fetchAuthData = async() => {
      const authData = AuthData.getAuthData();
      const url = `${authData.appFunctionUrl}/api/GetAuthDataFunction?code=${authData.azureFunctionKey}`;
      const data = await Fetch(url, authData.clientId);    
      const googleIdp = data.find(idp => idp.name.toLowerCase() === 'google');
      const clientId = googleIdp?.value;

      setClientId(clientId);
      setLoading(false);
    }
    
    fetchAuthData();

    // Rotate quotes every 5 seconds
    const quoteInterval = setInterval(() => {
      setCurrentQuote(prev => (prev + 1) % ecommerceQuotes.length);
    }, 5000);

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
      clearInterval(quoteInterval);
    };
  }, []);

  if (loading) {
    return <SvgLoader />;
  }

  if (!clientId) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <p style={styles.errorText}>Google authentication is not configured</p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div style={styles.container}>
        {/* Left Column - Login */}
        <div style={styles.leftColumn}>
          <div style={styles.loginCard}>
            {/* Logo */}
            <div style={styles.logoContainer}>
              <img
                src="/logo.svg"
                alt="Company Logo"
                style={styles.logo}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{...styles.logoFallback, display: 'none'}}>
                <span style={styles.logoText}>CMS</span>
              </div>
            </div>

            {/* Welcome Section */}
            <div style={styles.welcomeSection}>
              <h1 style={styles.welcomeTitle}>Welcome Back!</h1>
              <p style={styles.subtitleText}>Sign in to continue your journey</p>
            </div>

            {/* Messages */}
            {!errorMessage && message && (
              <div style={styles.messageBox}>
                <span style={styles.messageIcon}>💡</span>
                <p style={styles.messageText}>{message}</p>
              </div>
            )}
            {errorMessage && (
              <div style={styles.errorBox}>
                <span style={styles.errorIconSmall}>⚠️</span>
                <p style={styles.errorMessageText}>{errorMessage}</p>
              </div>
            )}

            {/* Login Button */}
            <div style={styles.loginButtonSection}>
              <GoogleLogin
                onSuccess={credentialResponse => {
                  login(credentialResponse);
                  navigate(redirectPath || '/');
                }}
                onError={() => {
                  setErrorMessage('Login failed. Please try again!');
                }}
                shape="rectangular"
                size="large"
                width="300"
                theme="outline"
              />
            </div>

            {/* Security Footer */}
            <div style={styles.securitySection}>
              <div style={styles.securityBadge}>
                <span style={styles.lockIcon}>🔒</span>
                <span style={styles.securityText}>Secure SSL Connection</span>
              </div>
            </div>

            {/* Footer */}
            <p style={styles.footerText}>© 2025 Content Management System</p>
          </div>
        </div>

        {/* Right Column - Motivation */}
        <div style={styles.rightColumn}>
          <div style={styles.motivationContent}>
            {/* Decorative elements */}
            <div style={styles.decorativeShape1}></div>
            <div style={styles.decorativeShape2}></div>
            <div style={styles.decorativeShape3}></div>

            {/* Main Headline */}
            <div style={styles.headlineSection}>
              <h2 style={styles.mainHeadline}>Grow Your E-Commerce</h2>
              <p style={styles.tagline}>Where Great Products Meet Happy Customers</p>
            </div>

            {/* Quote Section */}
            <div style={styles.quoteCard}>
              <div style={styles.quoteIcon}>"</div>
              <p style={styles.quoteText}>{ecommerceQuotes[currentQuote].quote}</p>
              <p style={styles.quoteTagline}>{ecommerceQuotes[currentQuote].tagline}</p>
              <div style={styles.quoteDots}>
                {ecommerceQuotes.map((_, index) => (
                  <span 
                    key={index} 
                    style={{
                      ...styles.quoteDot,
                      ...(index === currentQuote ? styles.quoteDotActive : {})
                    }}
                  ></span>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div style={styles.benefitsSection}>
              <div style={styles.benefitItem}>
                <span style={styles.benefitIcon}>✨</span>
                <span style={styles.benefitText}>Effortless Control</span>
              </div>
              <div style={styles.benefitItem}>
                <span style={styles.benefitIcon}>🚀</span>
                <span style={styles.benefitText}>Accelerate Growth</span>
              </div>
              <div style={styles.benefitItem}>
                <span style={styles.benefitIcon}>🎯</span>
                <span style={styles.benefitText}>Achieve More</span>
              </div>
            </div>

            {/* Motivational Message */}
            <div style={styles.motivationalBox}>
              <p style={styles.motivationalText}>
                <strong>Your future success starts here.</strong> Every thriving online store 
                was built one step at a time. Take control of your e-commerce journey today.
              </p>
            </div>
          </div>
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    width: '100vw',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    fontFamily: "'Segoe UI', 'Roboto', sans-serif",
  },
  
  // Left Column Styles
  leftColumn: {
    flex: '0 0 45%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    padding: '40px',
    boxSizing: 'border-box',
  },
  loginCard: {
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
  },
  logoContainer: {
    marginBottom: '32px',
  },
  logo: {
    height: '60px',
    width: 'auto',
  },
  logoFallback: {
    width: '80px',
    height: '80px',
    margin: '0 auto',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(30, 41, 59, 0.4)',
    border: '1px solid rgba(255, 215, 0, 0.2)',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: '2px',
  },
  welcomeSection: {
    marginBottom: '32px',
  },
  welcomeTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 8px 0',
    letterSpacing: '-0.5px',
  },
  subtitleText: {
    fontSize: '16px',
    color: '#64748b',
    margin: '0',
  },
  messageBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid #93c5fd',
  },
  messageIcon: {
    fontSize: '18px',
  },
  messageText: {
    color: '#1e40af',
    fontSize: '14px',
    margin: '0',
    fontWeight: '500',
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 20px',
    background: '#fef2f2',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid #fecaca',
  },
  errorIconSmall: {
    fontSize: '18px',
  },
  errorMessageText: {
    color: '#dc2626',
    fontSize: '14px',
    margin: '0',
    fontWeight: '500',
  },
  loginButtonSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  securitySection: {
    marginBottom: '24px',
  },
  securityBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#ecfdf5',
    borderRadius: '20px',
    border: '1px solid #a7f3d0',
  },
  lockIcon: {
    fontSize: '14px',
  },
  securityText: {
    fontSize: '13px',
    color: '#059669',
    fontWeight: '500',
  },
  footerText: {
    fontSize: '12px',
    color: '#94a3b8',
    margin: '0',
  },

  // Right Column Styles
  rightColumn: {
    flex: '0 0 55%',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
  },
  motivationContent: {
    position: 'relative',
    zIndex: 10,
    maxWidth: '500px',
    color: '#ffffff',
  },
  decorativeShape1: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(217, 119, 6, 0.15) 0%, transparent 70%)',
    top: '-100px',
    right: '-50px',
  },
  decorativeShape2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(5, 150, 105, 0.1) 0%, transparent 70%)',
    bottom: '10%',
    left: '-80px',
  },
  decorativeShape3: {
    position: 'absolute',
    width: '150px',
    height: '150px',
    borderRadius: '30px',
    background: 'radial-gradient(circle, rgba(245, 158, 11, 0.08) 0%, transparent 70%)',
    top: '40%',
    right: '10%',
    transform: 'rotate(45deg)',
  },
  headlineSection: {
    marginBottom: '40px',
    textAlign: 'center',
  },
  mainHeadline: {
    fontSize: '42px',
    fontWeight: '800',
    margin: '0 0 12px 0',
    lineHeight: '1.2',
    textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
  },
  tagline: {
    fontSize: '18px',
    margin: '0',
    opacity: '0.9',
    fontWeight: '400',
  },
  quoteCard: {
    background: 'linear-gradient(135deg, rgba(55, 65, 81, 0.6) 0%, rgba(75, 85, 99, 0.4) 100%)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '30px',
    marginBottom: '32px',
    border: '1px solid rgba(255, 215, 0, 0.15)',
    position: 'relative',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
  },
  quoteIcon: {
    position: 'absolute',
    top: '10px',
    left: '20px',
    fontSize: '60px',
    opacity: '0.4',
    fontFamily: 'Georgia, serif',
    lineHeight: '1',
    color: '#d97706',
  },
  quoteText: {
    fontSize: '28px',
    lineHeight: '1.4',
    margin: '0 0 12px 0',
    fontWeight: '700',
    position: 'relative',
    zIndex: '1',
    letterSpacing: '-0.5px',
  },
  quoteTagline: {
    fontSize: '15px',
    margin: '0 0 16px 0',
    opacity: '0.85',
    fontWeight: '400',
    fontStyle: 'italic',
  },
  quoteDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
  },
  quoteDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s ease',
    display: 'inline-block',
  },
  quoteDotActive: {
    background: '#f59e0b',
    transform: 'scale(1.2)',
    boxShadow: '0 0 8px rgba(245, 158, 11, 0.5)',
  },
  benefitsSection: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '32px',
    gap: '16px',
  },
  benefitItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(55, 65, 81, 0.5) 0%, rgba(75, 85, 99, 0.3) 100%)',
    borderRadius: '16px',
    flex: '1',
    transition: 'transform 0.3s ease',
    border: '1px solid rgba(255, 215, 0, 0.1)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
  benefitIcon: {
    fontSize: '28px',
  },
  benefitText: {
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'center',
    color: '#f1f5f9',
  },
  motivationalBox: {
    background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(5, 150, 105, 0.25)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },
  motivationalText: {
    fontSize: '15px',
    lineHeight: '1.7',
    margin: '0',
    textAlign: 'center',
    color: '#f1f5f9',
  },

  // Error Card
  errorCard: {
    background: '#ffffff',
    borderRadius: '20px',
    padding: '48px',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    border: '1px solid #fecaca',
  },
  errorIcon: {
    fontSize: '56px',
    marginBottom: '20px',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '18px',
    margin: '0',
    fontWeight: '500',
  },
};

// Add responsive styles via media query simulation
if (typeof window !== 'undefined' && window.innerWidth < 900) {
  styles.container.flexDirection = 'column';
  styles.leftColumn.flex = '1';
  styles.rightColumn.flex = '1';
  styles.rightColumn.display = 'none';
}

export default Login;
